import { z } from 'zod';
export const HobbiesFeatures = z.object({ 'feature.hobbies_v2': z.boolean() });
export const HobbyTimeZone = z
    .string()
    .max(100)
    .refine((value) => {
    try {
        new Intl.DateTimeFormat('en', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}, 'Invalid time zone');
export const HobbiesQuery = z.object({ timeZone: HobbyTimeZone.default('UTC') });
export const HobbyProgressQuery = HobbiesQuery.extend({
    weeks: z.coerce.number().int().min(1).max(8).default(8),
});
export const HobbyGoal = z.object({
    target: z.number().positive().max(1_000_000_000),
    unit: z.string().trim().min(1).max(30),
});
export const TrackedHobby = z.object({
    trackingSource: z.enum(['trips', 'photos']).optional(),
    total: z.number().int().nonnegative().optional(),
    id: z.string(),
    name: z.string(),
    catalogId: z.string().optional(),
    sort: z.number().int(),
    unit: z.string(),
    goal: HobbyGoal.nullable(),
});
export const HobbyWeek = z.object({
    start: z.iso.date(),
    end: z.iso.date(),
    timeZone: HobbyTimeZone,
});
export const HobbyWeekProgress = z.object({
    hobbyId: z.string(),
    amount: z.number().nonnegative(),
    logCount: z.number().int().nonnegative(),
});
export const HobbiesProfile = z.object({
    hobbies: z.array(TrackedHobby).max(20),
    week: HobbyWeek,
    progress: z.array(HobbyWeekProgress).max(20),
});
export const AddHobbyInput = z
    .object({
    timeZone: HobbyTimeZone.optional(),
    catalogId: z.string().min(1).optional(),
    customName: z.string().trim().min(1).max(30).optional(),
})
    .refine((value) => Boolean(value.catalogId) !== Boolean(value.customName), 'Choose a catalog hobby or a custom name');
export const SortHobbyInput = z.object({ sort: z.number().int().min(0).max(19) });
export const HobbyLogInput = z.object({
    amount: z.number().positive().max(1_000_000_000),
    loggedOn: z.iso.date(),
    note: z.string().trim().max(500).optional(),
    timeZone: HobbyTimeZone.default('UTC'),
});
export const HobbyLog = z.object({
    id: z.string(),
    hobbyId: z.string(),
    amount: z.number().positive(),
    unit: z.string(),
    loggedOn: z.iso.date(),
    note: z.string().optional(),
});
export const HobbyProgress = z.object({
    hobby: TrackedHobby,
    week: HobbyWeek,
    logs: z.array(HobbyLog),
    history: z
        .array(HobbyWeek.extend({
        amount: z.number().nonnegative(),
        logCount: z.number().int().nonnegative(),
    }))
        .max(8),
});
export const HobbyDeleted = z.object({ deleted: z.literal(true) });
export const HobbyCatalogItem = z.object({
    id: z.string().min(1),
    slug: z.string().min(1),
    name: z.string().min(1).max(30),
    defaultUnits: z.array(z.string().min(1)).min(1),
    icon: z.string().min(1),
    sort: z.number().int().nonnegative(),
    /** Explicit import can use this link; tracking never rewrites interests. */
    interestId: z.string().optional(),
});
export const GeoRegion = z.enum(['Americas', 'Europe', 'Asia', 'Africa', 'Oceania']);
export const GeoCountry = z.object({
    code: z.string().regex(/^[A-Z]{2}$/),
    name: z.string().min(1),
    region: GeoRegion,
    flag: z.string().min(1),
    countsAsNation: z.boolean(),
    subdivisionCount: z.number().int().positive(),
});
export const GeoSubdivision = z.object({
    code: z.string().min(2),
    countryCode: z.string().regex(/^[A-Z]{2}$/),
    name: z.string().min(1),
    /** A country without ISO subdivisions is itself one countable place. */
    isFallback: z.boolean(),
});
export const GeoCountries = z.object({
    datasetVersion: z.string().min(1),
    totalNations: z.number().int().positive(),
    totalSubdivisions: z.number().int().positive(),
    countries: z.array(GeoCountry),
});
export const GeoSubdivisions = z.object({
    datasetVersion: z.string().min(1),
    countryCode: z.string().regex(/^[A-Z]{2}$/),
    subdivisions: z.array(GeoSubdivision).min(1),
});
/** A read-only preview of upcoming activities in the viewer's joined Hobbies groups. */
export const HobbyMeetup = z.object({
    id: z.string(),
    source: z.enum(['event', 'meeting']),
    communityId: z.string(),
    communityName: z.string(),
    title: z.string(),
    startsAt: z.coerce.date(),
    timezone: z.string(),
    placeLabel: z.string(),
    photoUrl: z.string().nullable(),
});
//# sourceMappingURL=hobbies.js.map